import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom'
import './assets/scss/style.scss'
import Spinner from './views/spinner/Spinner'
// Activar timeout 120s para peticiones a chronus-plane (antes de cargar la app)
import './utils/planeRequest'
import axios from 'axios'
import keycloak from './keycloak'
import API from './api'
import { getUser as getDtMoodUser, saveDtMoodSession } from './modules/dt-mood/services'
// Interceptor global de axios (Bearer token + redirect a login en 401) --
// se importa una sola vez acá, antes de renderizar nada. Ver src/authInterceptor.js.
import './authInterceptor'

const App = lazy(
  () =>
    new Promise((resolve) => {
      setTimeout(() => resolve(import('./app')), 0)
    })
)

function render () {
  ReactDOM.render(
    <Suspense fallback={<Spinner />}>
      <App />
    </Suspense>,
    document.getElementById('root')
  )
}

// Login único vía Keycloak (reemplaza la cadena Basecamp -> DTmood -> Google
// que vivía en Login.js). 'login-required': si no hay sesión, redirige
// directo a Keycloak -- no renderizamos nada de la app hasta volver
// autenticados. Ver CLAUDE.md.
//
// Sin silentCheckSsoRedirectUri a propósito: esa optimización evita un
// parpadeo de redirect cuando ya existe sesión, pero depende de un iframe
// que necesita cookies de terceros -- en navegadores que las bloquean
// agresivo (Brave, Safari con ITP) el chequeo nunca resuelve y la SPA
// queda tildada en blanco reintentando ("Timeout when waiting for 3rd
// party check iframe message", en loop). Con 'login-required' no hace
// falta: si no hay sesión, igual redirige directo (navegación de
// página completa, no un iframe, no le afecta el bloqueo de cookies).
keycloak
  .init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false
  })
  .then((authenticated) => {
    if (!authenticated) {
      keycloak.login()
      return
    }
    // Nombre/foto de perfil: antes venían del JWT de Google que devolvía el
    // login viejo (google_name/google_foto en localStorage, ver
    // Login.js/Sidebar.js). Keycloak federa Google como Identity Provider,
    // así que esos mismos claims -- si el realm los mapea -- vienen en el
    // propio token de Keycloak. Ojo: `picture` suele venir mapeado solo en
    // el ID token, no en el access token -- keycloak.tokenParsed es el
    // access token, keycloak.idTokenParsed es el ID token. Se combinan los
    // dos (ID token primero, es donde vive el perfil) para no depender de
    // en cuál de los dos haya quedado configurado el mapper.
    const claims = { ...(keycloak.tokenParsed || {}), ...(keycloak.idTokenParsed || {}) }
    if (claims.name) window.localStorage.setItem('google_name', claims.name)
    if (claims.picture) window.localStorage.setItem('google_foto', claims.picture)

    // "whoami" de DTmood: resuelve groupId/userId/clientId (IDs internos de
    // DTmood, no vienen en el token de Keycloak) -- antes esto pasaba en
    // LoginDtMood.jsx después de un login propio de DTmood; ahora que DTmood
    // acepta el token de Keycloak directamente, alcanza con pedirlo por
    // email una vez acá. Best-effort: si falla (DTmood caído, o el email no
    // tiene cuenta en DTmood) no bloqueamos el resto de la app -- solo
    // degradan las pantallas de DTmood, que ya manejan sesión ausente.
    if (claims.email) {
      getDtMoodUser(claims.email)
        .then((res) => {
          saveDtMoodSession(
            keycloak.token,
            0,
            res?.group?.id,
            res?.id,
            res?.username,
            res?.client?.id,
            res?.email,
            res?.firstName,
            res?.lastName,
            ''
          )
        })
        .catch((err) => console.error('Error resolviendo el usuario de DTmood', err))
    }

    // Resuelve el Colaborador vinculado al JWT (JIT/auto-link ya corrido
    // del lado del backend, ver GET /chronus/me) y llena localStorage con
    // las mismas keys que ya leen ~14 archivos existentes (Sidebar,
    // FullLayout, Dashboard, usePermisoRuta, pantallas de SOW, etc.) --
    // así no hace falta tocar esos archivos, solo cambia de dónde sale el
    // dato (antes: escaneo client-side de /colaboradores por email; ahora:
    // resuelto y validado por el backend contra el token).
    axios
      .get(`${API.url}/me`)
      .then((res) => {
        const colaborador = res.data.data
        window.localStorage.setItem('id', colaborador.id)
        window.localStorage.setItem('rol', colaborador.rol)
        window.localStorage.setItem('horas_esperadas', colaborador.horas_esperadas)
        if (colaborador.codigo_basecamp) {
          window.localStorage.setItem('codigo_basecamp', colaborador.codigo_basecamp)
        }
        render()
      })
      .catch((err) => {
        // 403 = usuario autenticado en Keycloak pero sin Colaborador
        // vinculado (ver app/auth.py del backend) -- no hay nada más que
        // reintentar acá, hace falta que RR.HH. cree/vincule el registro.
        // No renderizamos la app (quedaría rota sin id/rol en localStorage).
        console.error('Error resolviendo /chronus/me', err)
        const mensaje =
          err?.response?.status === 403
            ? err.response.data?.message ||
              'Tu usuario no está registrado en Chronus. Contactá a RR.HH.'
            : 'No se pudo conectar con Chronus. Intentá de nuevo más tarde.'
        const emailMatch = mensaje.match(/[\w.+-]+@[\w-]+\.[\w.-]+/)

        ReactDOM.render(
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
              background: '#f5f6fa'
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                padding: '2.5rem',
                maxWidth: 480,
                textAlign: 'center'
              }}
            >
              <h2 style={{ marginTop: 0 }}>No pudimos abrir Chronus</h2>
              <p style={{ color: '#555' }}>{mensaje}</p>
              {emailMatch && (
                <p>
                  <a href={`mailto:${emailMatch[0]}`}>{emailMatch[0]}</a>
                </p>
              )}
              <button
                type='button'
                onClick={() => keycloak.logout({ redirectUri: window.location.origin })}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1.25rem',
                  border: 'none',
                  borderRadius: 4,
                  background: '#6c5ce7',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Probar con otra cuenta
              </button>
            </div>
          </div>,
          document.getElementById('root')
        )
      })
  })
  .catch((err) => {
    console.error('Error inicializando Keycloak', err)
  })

// Refresco automático del token -- si falla (p. ej. el refresh token
// también venció), fuerza un login nuevo en vez de dejar requests
// autenticadas con un token vencido.
keycloak.onTokenExpired = () => {
  keycloak.updateToken(30).catch(() => keycloak.login())
}
